// src/lib/plants.js
// Phase 37 — extracted from src/App.jsx (lines 24–52, 71–73, 575–578,
// 2973–2985, 3262–3270).
//
// Scope: plant data validation + transforms + suggestion lookup.
// Public exports: PLANT_SUGGESTIONS_BY_LATIN, enrichGrowingConditions,
//                 nextPlantId, isValidPlant, normalisePendingPlant, normalisePlant.
// Module state: PLANT_SUGGESTIONS_BY_LATIN — built once at module scope.
//                Vite ESM ensures one execution per module URL — singleton safe.

import { PLANT_SUGGESTIONS } from '../data/suggestions.js';

// ENG-01 — O(1) render-time lookup for growing-conditions (sun/soil/hardiness).
// Built once at module scope so PlantDirectoryCard doesn't linear-scan the
// 2007-entry PLANT_SUGGESTIONS array on every render (RESEARCH Pitfall 3).
export const PLANT_SUGGESTIONS_BY_LATIN = new Map(
  (PLANT_SUGGESTIONS || []).map(s => [s.latin, s])
);

// ENG-01 — enrich a plant record with sun/soil/hardiness from PLANT_SUGGESTIONS
// when latin (preferred) or name matches. Preserves any fields already set on
// the plant (never overwrites). Used by AddPlantForm static-lookup branch,
// Ask Tend fetchScheduleAndAdd, and BulkImportPanel to persist growing
// conditions at add time (D-02 hybrid persistence).
export function enrichGrowingConditions(plant) {
  if (!plant) return plant;
  let suggestion = null;
  if (plant.latin) {
    suggestion = PLANT_SUGGESTIONS_BY_LATIN.get(plant.latin) || null;
  }
  if (!suggestion && plant.name) {
    const lower = plant.name.toLowerCase();
    suggestion = (PLANT_SUGGESTIONS || []).find(
      s => (s.name || "").toLowerCase() === lower
    ) || null;
  }
  if (!suggestion) return plant;
  return {
    ...plant,
    ...(suggestion.sun       && !plant.sun       ? { sun: suggestion.sun }             : {}),
    ...(suggestion.soil      && !plant.soil      ? { soil: suggestion.soil }           : {}),
    ...(suggestion.hardiness && !plant.hardiness ? { hardiness: suggestion.hardiness } : {}),
  };
}

// Centralised plant ID generation — used by all three add-plant paths
export function nextPlantId(plants) {
  return Math.max(0, ...plants.map(p => p.id)) + 1;
}

// Validate a plant object has the required fields (used in localStorage read and tests)
export function isValidPlant(p) {
  return !!(p && typeof p.id === "number" && typeof p.name === "string"
    && p.name.length > 0 && Array.isArray(p.locations));
}

// Normalise a plant object parsed from Ask Tend's PLANT_ADD JSON so that
// `wiki` is never empty when latin or name is available — mirrors the form
// path fallback at src/App.jsx:2701. Pure, no side effects, safe for unit test.
// (#206 / D-04.2 / D-04.3)
//
// PR #297 — additional resilience: prefer PLANT_SUGGESTIONS.wiki when the
// plant's latin matches a known suggestion. The AI sometimes returns a
// weak `wiki` value (or omits it) for plants that exist in our 2,000-entry
// suggestions DB; using the suggestion's curated wiki key gives the same
// image-resolution quality as the standard add-plant form (which doesn't
// hit this code path because the form's wiki comes from the suggestion
// dropdown directly).
export function normalisePendingPlant(p) {
  if (!p || typeof p !== 'object') return p;
  const trimmedLatin = typeof p.latin === 'string' ? p.latin.trim() : null;
  const trimmedName  = typeof p.name === 'string'  ? p.name.trim()  : null;
  const trimmedWiki  = (p.wiki && String(p.wiki).trim()) || '';

  // 1. Look up in PLANT_SUGGESTIONS by latin (preferred) or name. Curated
  //    suggestion.wiki is the highest-quality Wikipedia key we have.
  let suggestion = null;
  if (trimmedLatin) suggestion = PLANT_SUGGESTIONS_BY_LATIN.get(trimmedLatin) || null;
  if (!suggestion && trimmedName) {
    const lower = trimmedName.toLowerCase();
    suggestion = (PLANT_SUGGESTIONS || []).find(
      s => (s.name || '').toLowerCase() === lower
    ) || null;
  }

  // 2. Resolve wiki — preference order:
  //    - explicit non-empty wiki the AI / user gave us
  //    - suggestion.wiki when a curated entry matches
  //    - latin
  //    - name
  const wiki = trimmedWiki
    || (suggestion && suggestion.wiki)
    || trimmedLatin
    || trimmedName
    || '';

  return {
    ...p,
    name: trimmedName,
    latin: trimmedLatin,
    wiki,
  };
}

// parseLatin — Phase 41 Plan 02 (DATA-02)
// Deterministic parser for botanical latin names. Pure function — no async,
// no AI fallback, no side effects. Same function used by build-time codemod
// (Node) and v9→v10 migration (browser). See 41-CONTEXT.md D-07..D-08.
//
// Rules:
//   - First token after optional leading × → genus (leading × joined: "× Cuprocyparis")
//   - Second lowercase token → species (hybrid × preserved: "× soulangeana")
//   - First '…'-quoted substring → cultivar (quotes stripped per D-12)
//   - subsp. / var. / f. / cv. qualifiers and trailing author strings dropped
//   - Parenthetical author ("(L.)", "(David Austin)") stops species parse
//   - Unparseable fields left null — never throws
export function parseLatin(latin) {
  if (!latin || typeof latin !== 'string') return { genus: null, species: null, cultivar: null };
  const s = latin.trim();
  if (!s) return { genus: null, species: null, cultivar: null };

  // 1. Extract cultivar from first '…' quoted substring (strip quotes per D-12)
  const cultivarMatch = s.match(/'([^']+)'/);
  const cultivar = cultivarMatch ? cultivarMatch[1] : null;

  // 2. Strip cultivar portion for genus/species parsing
  const withoutCultivar = s.replace(/'[^']*'/, '').trim();

  // 3. Tokenise — split on whitespace
  const QUALIFIERS = new Set(['subsp.', 'var.', 'f.', 'cv.', 'nothovar.']);
  const tokens = withoutCultivar.split(/\s+/).filter(Boolean);

  let genus = null;
  let species = null;
  let i = 0;

  // 3a. Handle optional leading × (notho-genus marker)
  if (tokens[i] === '×') {
    // Leading × — join with the next token to form the notho-genus
    i++; // skip the × token
    if (i < tokens.length) {
      genus = '× ' + tokens[i];
      i++;
    }
  } else if (i < tokens.length && /^[A-Z]/.test(tokens[i])) {
    // 3b. First capitalised token = genus
    genus = tokens[i];
    i++;
  }

  // 3c. Species candidate: next token(s)
  while (i < tokens.length) {
    const t = tokens[i];
    if (QUALIFIERS.has(t)) {
      // Skip qualifier AND its following value token
      i += 2;
      continue;
    }
    if (/^\(/.test(t)) break;                         // parenthetical author — stop
    if (/^[A-Z]/.test(t) && t.endsWith('.')) break;  // author abbreviation — stop
    if (t === '×') {
      // Hybrid × in species position — join with the next lowercase token
      const next = tokens[i + 1];
      if (next && /^[a-z]/.test(next)) {
        species = '× ' + next;
        i += 2;
      } else {
        i++;
      }
      break;
    }
    if (/^[a-z]/.test(t)) {
      species = t;
      i++;
      break;
    }
    break; // anything else stops the parse
  }

  return { genus, species, cultivar };
}

// Helper: normalise a plant to use locations[] (migrates old area/subArea fields)
export function normalisePlant(p) {
  if (p.locations) return p; // already migrated
  return {
    ...p,
    locations: [{ areaId: p.area || "a1", subArea: p.subArea || "" }],
    area: undefined,
    subArea: undefined,
  };
}
